/**
 * Demolitions — supersonic contact, and the respawn that follows.
 *
 * THE RULE, and the thing most reimplementations get wrong: there is no
 * relative-speed demolition threshold anywhere in the sources. docs/
 * CONSTANTS_SOURCES.md §6 states it outright — "There is no relative-speed demo
 * threshold. The rule is: closing + front-bumper contact + attacker's
 * supersonic FLAG + opposing teams." So all four of these must hold, in order:
 *
 *   1. the attacker is CLOSING on the victim — see `approachRates`, which is
 *      RocketSim's test and is NOT quite the relative-velocity one;
 *   2. contact is on the FRONT BUMPER: the contact point, in the attacker's
 *      local frame, has x > BUMP_MIN_FORWARD_DIST (64.5);
 *   3. the attacker's `supersonic` FLAG is set. Not `speed >= 2200` — the flag,
 *      which carries RocketSim's maintain hysteresis (see `updateSupersonic`
 *      in ./boost). A car that decelerates from 2250 to 2150 is still
 *      supersonic for up to a second, and still demolishes;
 *   4. the two cars are on opposing teams. Team demos are a mutator and are
 *      off here, so teammates are NEVER demolished.
 *
 * Rules 1 and 2 are shared verbatim with the BUMP, which is what a contact that
 * passes them but fails 3 or 4 produces. Both live in ./car-car, which owns the
 * single ordered-pair loop RocketSim's `_BtCallback_OnCarCarCollision` is, plus
 * the rigid-body response underneath it. THIS file owns only the predicates and
 * the kill/respawn half; it deliberately imports nothing from ./car-car so that
 * the dependency runs one way.
 * [1-src: RS Arena.cpp. RU does not model demolition at all.]
 *
 * Car-car overlap is a separating-axis test between the two oriented hitboxes
 * (`obbOverlap` in ./obb, shared with the contact solver). The contact point for
 * rule 2 is approximated as the point of the ATTACKER's box nearest the victim's
 * box centre; see `contactPointLocal` for why that, and not the solver's
 * manifold, is the right input to a bumper test.
 */

import { RESPAWN_SPAWNS } from './arena-data';
import { resetBoostBurst } from './boost';
import type { CarState } from './car-state';
import { BOOST_SPAWN_AMOUNT, BUMP_MIN_FORWARD_DIST, DEMO_RESPAWN_TIME } from './constants';
import {
  clamp,
  clone,
  dot,
  eulerToRotation,
  normalize,
  sub,
  type Vec3,
  vec3,
} from './math';
import { carHitbox, obbOverlap, toLocal } from './obb';

/** Same accumulated-float slack as ./boost uses on its timers: a respawn timer
 *  stepped 360 times by 1/120 can land a few ulps either side of zero. */
const TIME_EPSILON = 1e-9;

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * The car's collision box in world space. The box centre is offset from the
 * car's origin (which is the centre of mass) by the BODY's `hitboxOffset` in
 * the car's local frame — see ./car-config, and note that both the offset and
 * the extents differ per body, so the front-bumper band of rule 2 is not the
 * same band on a Dominus as on an Octane.
 * [conflict: RS's extents are 2.5 uu larger on every axis than RU's; we follow
 *  ./car-config, which takes RS. See docs/CONSTANTS_SOURCES.md §U1.]
 */
export const carBox = (car: CarState) => carHitbox(car.position, car.orientation, car.config);

/** True when the two cars' hitboxes intersect. */
export const carsOverlap = (a: CarState, b: CarState): boolean =>
  obbOverlap(carBox(a), carBox(b));

/**
 * Approximate contact point, in the ATTACKER's local frame and relative to the
 * attacker's ORIGIN (which is what BUMP_MIN_FORWARD_DIST is measured from, not
 * the hitbox centre).
 *
 * Computed as the point of the attacker's own box closest to the victim's box
 * centre. The attacker's front face sits at local x = 13.8757 + 60.2535 =
 * 74.13, so the bumper band is the front ~9.6 uu of the car — a head-on hit
 * clears 64.5 comfortably while a side or rear hit does not.
 *
 * WHY NOT THE SOLVER'S MANIFOLD, now that ./car-car produces one. RocketSim's
 * test is on a Bullet manifold point, and Bullet's manifold is the CLIPPED
 * contact polygon — for a T-bone it covers only the few uu that actually touch.
 * Our manifold is the attacker box's unclipped support set, so a side-on hit
 * would contribute the two front corners of the side face at local x = 74.13 and
 * report a bumper hit for a contact nowhere near the bumper. The closest-point
 * rule has no such failure mode: it is a single point, it lands on whichever
 * face is actually facing the victim, and that IS the discrimination rule 2
 * needs.
 */
export const contactPointLocal = (attacker: CarState, victim: CarState): Vec3 => {
  const a = carBox(attacker);
  const local = toLocal(a, carBox(victim).center);
  const h = a.halfExtents;
  const o = attacker.config.hitboxOffset;
  return vec3(
    clamp(local.x, -h.x, h.x) + o.x,
    clamp(local.y, -h.y, h.y) + o.y,
    clamp(local.z, -h.z, h.z) + o.z
  );
};

/**
 * The relative-velocity form of rule 1: the attacker's speed toward the victim
 * minus the victim's speed away from the attacker, both projected on the
 * direction to the victim. Positive means closing.
 *
 * THIS IS NOT THE RULE THE GAME APPLIES — see `approachRates`, which projects
 * the two velocities on DIFFERENT directions and is what the source does. This
 * remains as the intuitive quantity a caller outside the physics (a bot, an
 * impact sound) actually wants; nothing in the demo or bump path reads it.
 * Exactly coincident cars give 0 (not closing).
 */
export const closingSpeed = (attacker: CarState, victim: CarState): number => {
  const dir = normalize(sub(victim.position, attacker.position));
  return dot(sub(attacker.velocity, victim.velocity), dir);
};

/**
 * Rule 1, verbatim, plus the two directions and the curve input the BUMP needs.
 * Returns null when the attacker is not closing on the victim.
 *
 * The source:
 *
 *     Vec deltaPos = otherState.pos - state.pos;
 *     if (state.vel.Dot(deltaPos) > 0) {
 *         Vec velDir = state.vel.Normalized();
 *         Vec dirToOtherCar = deltaPos.Normalized();
 *         float speedTowardsOtherCar = state.vel.Dot(dirToOtherCar);
 *         float otherCarAwaySpeed    = otherState.vel.Dot(velDir);
 *         if (speedTowardsOtherCar > otherCarAwaySpeed) { ... }
 *
 * NOTE THE TWO DIFFERENT DIRECTIONS. `speedTowardsOtherCar` projects the
 * attacker on the line joining the cars; `otherCarAwaySpeed` projects the VICTIM
 * on the attacker's own heading. They coincide only when the attacker is driving
 * straight at the victim, and the difference is not a rounding one: a victim
 * fleeing perpendicular to the attacker's heading counts as stationary here and
 * as fast-moving under `closingSpeed`. This function is the rule; that one is
 * the intuition.
 *
 * The outer `vel.Dot(deltaPos) > 0` guard is also what makes the two
 * normalizations safe — neither vector can be zero past it.
 */
export interface ApproachRates {
  /** Unit attacker velocity. The bump's push direction. */
  velDir: Vec3;
  /** Unit attacker -> victim. */
  dirToOther: Vec3;
  /** Attacker's speed along `dirToOther`. The bump curves' input. */
  speedTowards: number;
  /** Victim's speed along `velDir`. */
  otherAwaySpeed: number;
}

export const approachRates = (attacker: CarState, victim: CarState): ApproachRates | null => {
  const deltaPos = sub(victim.position, attacker.position);
  if (dot(attacker.velocity, deltaPos) <= 0) return null;

  const velDir = normalize(attacker.velocity);
  const dirToOther = normalize(deltaPos);
  const speedTowards = dot(attacker.velocity, dirToOther);
  const otherAwaySpeed = dot(victim.velocity, velDir);
  if (speedTowards <= otherAwaySpeed) return null;

  return { velDir, dirToOther, speedTowards, otherAwaySpeed };
};

/**
 * Rules 3 and 4 on their own — everything in the demolition rule that is not
 * geometry, and the ONLY thing that separates a demo from a bump once a contact
 * has already cleared "closing" and "front bumper".
 *
 * `teamDemos` is RocketSim's `MutatorConfig::enableTeamDemos`, off in the
 * default game. Shared with ./car-car so that the two cannot disagree about
 * which contacts kill.
 */
export const demoQualifies = (attacker: CarState, victim: CarState, teamDemos = false): boolean =>
  attacker.supersonic && (teamDemos || attacker.team !== victim.team);

/**
 * All four demolition conditions. Pure — it decides, it does not apply.
 * Order is cheapest-first: team and flag tests before any geometry.
 */
export const canDemolish = (attacker: CarState, victim: CarState, teamDemos = false): boolean => {
  if (attacker === victim) return false;
  // A dead car neither demolishes nor can be demolished again.
  if (attacker.demolished || victim.demolished) return false;
  // Rules 3 and 4.
  if (!demoQualifies(attacker, victim, teamDemos)) return false;
  // Rule 1.
  if (approachRates(attacker, victim) === null) return false;
  // Contact.
  if (!carsOverlap(attacker, victim)) return false;
  // Rule 2 — front bumper only.
  return contactPointLocal(attacker, victim).x > BUMP_MIN_FORWARD_DIST;
};

// ---------------------------------------------------------------------------
// Applying a demolition, and the respawn
// ---------------------------------------------------------------------------

export interface DemoEvent {
  attacker: CarState;
  victim: CarState;
}

/**
 * Kill a car: it stops existing physically until `respawnTimer` runs out.
 * Boost is deliberately NOT touched here — the respawn sets it, and leaving it
 * alone means a UI can still show what the car died with.
 */
export const demolishCar = (car: CarState): void => {
  car.demolished = true;
  car.respawnTimer = DEMO_RESPAWN_TIME;
  car.velocity = vec3(0, 0, 0);
  car.angularVelocity = vec3(0, 0, 0);
  car.supersonic = false;
  car.supersonicTimer = 0;
  car.onGround = false;
  car.surface = 'none';
  car.carContact.otherCarIndex = -1;
  car.carContact.cooldownTimer = 0;
  resetBoostBurst(car);
};

/**
 * Put a car back on the field at one of its team's four respawn points, at
 * CAR_RESPAWN_Z = 36 (so it drops the last few uu), facing into the field, with
 * BOOST_SPAWN_AMOUNT = 33.33 boost and no velocity of any kind.
 *
 * RocketSim picks among the four spawns with a seeded RNG, and so do we: the
 * world draws the index from `World.rng` (./rng), which is part of the world's
 * serialized state, so the choice is random but perfectly reproducible. The
 * index stays an explicit parameter here rather than this module owning a
 * generator, because that is what lets a test pin a spawn. It defaults to 0
 * ("right inside") and is wrapped, so any integer is safe.
 */
export const respawnCar = (car: CarState, spawnIndex = 0): void => {
  const spawns = RESPAWN_SPAWNS[car.team];
  const n = spawns.length;
  const spawn = spawns[((spawnIndex % n) + n) % n];

  car.position = clone(spawn.position);
  car.velocity = vec3(0, 0, 0);
  car.angularVelocity = vec3(0, 0, 0);
  car.orientation = eulerToRotation(0, spawn.yaw, 0);

  car.boostAmount = BOOST_SPAWN_AMOUNT;
  car.supersonic = false;
  car.supersonicTimer = 0;
  resetBoostBurst(car);

  // Respawn height is above the resting height, so the car starts airborne.
  car.surface = 'none';
  car.surfaceNormal = vec3(0, 0, 1);
  car.onGround = false;

  car.hasJumped = false;
  car.hasFlip = true;
  car.isDodging = false;
  car.jumpHoldTime = 0;
  car.airTime = 0;
  car.dodgeTime = 0;
  car.dodgeDirection = vec3(0, 0, 0);
  car.dodgeCooldown = 0;

  car.carContact.otherCarIndex = -1;
  car.carContact.cooldownTimer = 0;

  car.demolished = false;
  car.respawnTimer = 0;
};

/**
 * Count down a demolished car's respawn timer and put it back when it expires.
 * A live car is untouched, so this is safe to call unconditionally every tick.
 */
export const stepDemo = (car: CarState, dt: number, spawnIndex = 0): void => {
  if (!car.demolished) return;
  car.respawnTimer -= dt;
  if (car.respawnTimer <= TIME_EPSILON) respawnCar(car, spawnIndex);
};

/**
 * Count every demolished car's respawn timer down and put back the ones that
 * expire. One tick, whole field.
 *
 * IT RUNS BEFORE CONTACTS ARE TESTED (see the tick order in src/sim/world.ts),
 * so a car demolished on tick T gets the full DEMO_RESPAWN_TIME of ticks before
 * it returns rather than losing the tick it died on.
 *
 * `spawnIndexOf` is called ONLY on the tick a car actually comes back, never
 * once per car per tick. That matters when the caller backs it with a seeded
 * generator: consuming a random number for every live car on every tick would
 * still be deterministic, but the stream would depend on how many cars happened
 * to be alive, which makes an episode impossible to reason about.
 */
export const stepRespawns = (
  cars: readonly CarState[],
  dt: number,
  spawnIndexOf?: (car: CarState, index: number) => number
): void => {
  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    if (!car.demolished) continue;
    car.respawnTimer -= dt;
    if (car.respawnTimer <= TIME_EPSILON) {
      respawnCar(car, spawnIndexOf ? spawnIndexOf(car, i) : 0);
    }
  }
};

/*
 * `stepDemolitions` USED TO LIVE HERE AND IS DELIBERATELY GONE.
 *
 * It ran its own pair sweep and applied kills independently of the car-vs-car
 * pass, which meant two demolition rules in one codebase. They did not agree:
 * this one said "two cars that demolish each other on the same tick both die",
 * whereas RocketSim's `Arena::_BtCallback_OnCarCarCollision` bails out of the
 * whole handler with `return` — not `continue` — the moment either car is
 * demolished, so a supersonic head-on kills exactly one. It also had no bump
 * cooldown, which the real rule uses to gate demos as well as bumps.
 *
 * The single rule now lives in ../physics/car-car (`stepCarCarCollisions`),
 * with respawn timers split out into `stepRespawns` above so the ordering
 * guarantee — timers before contacts, so a car killed on tick T is not charged
 * for the tick it died on — is still explicit.
 *
 * It was briefly restored from a build bundle by an agent that mistook its
 * deletion for file corruption. This note exists so that does not happen twice.
 */
